<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto">
        <form @submit.prevent="saveProject">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="w-full xl:w-1/2 mx-auto flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <h1 class="stage-page-title">Projekt bearbeiten</h1>
                    <div class="inline-flex">
                        <router-link class="btn-stage-secondary mr-3" :to="projectListPath">
                            {{ $t('Back') }}
                        </router-link>
                        <button class="btn-stage-primary" type="submit">
                            Projekt speichern
                        </button>
                    </div>
                </div>
            </div>
            <div class="stage-page-body">
                <div class="w-full xl:w-1/2 mx-auto bg-white shadow sm:rounded overflow-hidden">
                    <loading :status="loading.form"/>
                    <div class="p-6">
                        <div class="grid grid-cols-3 gap-6">
                            <div class="col-span-3">
                                <label class="stage-filter-label block mb-2" for="project-title">Titel</label>
                                <div class="relative">
                                    <input
                                        id="project-title"
                                        v-model="project.title"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        placeholder="Titel"
                                        required
                                    >
                                </div>
                            </div>
                            <div class="col-span-3">
                                <label class="stage-filter-label block mb-2" for="project-description">Projektbeschreibung</label>
                                <div class="relative">
                                    <textarea
                                        id="project-description"
                                        v-model="project.description"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        placeholder="Projektbeschreibung"
                                        rows="8"
                                    ></textarea>
                                </div>
                            </div>
                            <div class="col-span-3">
                                <label class="block text-sm leading-5 font-medium text-gray-700">Projekt-Bild</label>
                                <div class="mt-2 flex items-center select-none">
                                    <div class="avatar-editor relative inline-block h-12 w-12 rounded-sm overflow-hidden bg-gray-100">
                                        <button
                                            v-show="hasCustomImage"
                                            class="avatar-editor-remove absolute top-0 right-0 items-center p-0.5 rounded-full leading-4 bg-red-600 text-white cursor-pointer"
                                            type="button"
                                            @click="removeImage"
                                        >
                                            <svg-vue class="h-4 w-4 p-px" icon="font-awesome.times-light"></svg-vue>
                                        </button>
                                        <img
                                            :src="imagePreviewUrl"
                                            alt="Projektbild"
                                            class="h-full w-full text-gray-300 rounded-sm object-cover"
                                        />
                                    </div>
                                    <span class="ml-5">
                                        <input ref="changeImage" accept=".png,.jpg,.jpeg" hidden type="file" @change="changeImage($event)">
                                        <button
                                            class="py-2 px-3 border border-bordergray-100 rounded text-sm leading-4 font-medium text-gray-700 bg-white hover:text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-0 active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
                                            type="button"
                                            @click="selectImage"
                                        >
                                            Projektbild ändern
                                        </button>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="stage-footer-surface text-right px-6 py-3 border-t border-bordergray-100">
                        <span class="inline-flex">
                            <button class="btn btn-gray rounded shadow-sm" type="submit">
                                Projekt speichern
                            </button>
                        </span>
                    </div>
                </div>
                <div class="w-full xl:w-1/2 mx-auto bg-white shadow sm:rounded overflow-hidden mt-6">
                    <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">Projekt löschen</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                Das Projekt und alle zugehörigen Daten werden dauerhaft entfernt.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div v-if="!canDeleteProject">
                                <div class="text-base leading-6 text-gray-800">
                                    {{ $t('Delete protection is active because the following assignments still exist:') }}
                                </div>
                                <div v-if="deleteGuard.blockers.length > 0" class="mt-3 rounded border border-bordergray-100 overflow-hidden">
                                    <div
                                        v-for="blocker in deleteGuard.blockers"
                                        :key="'delete-project-guard-blocker-' + blocker.key"
                                        class="flex items-center justify-between gap-3 px-4 py-3 text-sm text-gray-700 border-t border-bordergray-100 first:border-t-0"
                                    >
                                        <span class="min-w-0">{{ blocker.label }}</span>
                                        <span class="whitespace-nowrap">{{ blocker.count }}</span>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                Das Projekt und alle zugehörigen Daten werden dauerhaft entfernt.
                            </div>
                        </div>
                    </div>
                    <div class="stage-footer-surface text-right px-6 py-3 border-t border-bordergray-100">
                        <span class="inline-flex">
                            <button
                                :disabled="!canDeleteProject"
                                :class="canDeleteProject ? 'btn btn-red rounded shadow-sm' : 'btn btn-gray rounded shadow-sm opacity-60 cursor-not-allowed'"
                                type="button"
                                @click="openDeleteProjectModal"
                            >
                                Projekt löschen
                            </button>
                        </span>
                    </div>
                </div>
            </div>
        </form>

        <confirm-dialog :open="deleteProjectModal" title="Projekt löschen" text="Soll dieses Projekt wirklich gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden." confirm-label="Projekt löschen" confirm-variant="danger" @close="deleteProjectModal = false" @confirm="deleteProject" />
    </main>
</template>

<script>
import ConfirmDialog from '@/components/modals/confirm-dialog.vue';
import {resolveReturnToPath} from "@/utils/list-route-state";

export default {
    components: {ConfirmDialog},
    name: 'dashboard-projects-edit',
    metaInfo() {
        return {
            title: 'Projekt bearbeiten',
        };
    },
    data() {
        return {
            deleteProjectModal: false,
            loading: {
                form: true,
            },
            imageRemoved: false,
            project: {
                id: null,
                title: '',
                description: '',
                image: null,
                image_preview: null,
                delete_guard: {
                    deletable: true,
                    message: '',
                    blockers: [],
                },
            },
        };
    },
    computed: {
        projectListPath() {
            return resolveReturnToPath(this.$route.query || {}, '/workspace/projects');
        },
        hasCustomImage() {
            return !!this.project.image_preview && !this.imageRemoved;
        },
        deleteGuard() {
            return this.project && this.project.delete_guard ? this.project.delete_guard : {
                deletable: true,
                message: this.$t('The project can be deleted.').toString(),
                blockers: [],
            };
        },
        canDeleteProject() {
            return !!this.deleteGuard.deletable;
        },
        imagePreviewUrl() {
            if (this.hasCustomImage) {
                return this.project.image_preview;
            }

            return this.generateFallbackImage();
        },
    },
    mounted() {
        this.getProject();
    },
    methods: {
        getProject() {
            const self = this;
            self.loading.form = true;

            axios.get('api/workspace/projects/' + self.$route.params.id).then(function (response) {
                const projectData = response.data.project || response.data.data || response.data || {};

                self.project.id = projectData.id || null;
                self.project.title = projectData.title || '';
                self.project.description = projectData.description || '';
                self.project.image = null;
                self.project.image_preview = projectData.image_url || null;
                self.project.delete_guard = projectData.delete_guard || {
                    deletable: true,
                    message: self.$i18n.t('The project can be deleted.').toString(),
                    blockers: [],
                };
                self.imageRemoved = false;
                self.loading.form = false;
            }).catch(function () {
                self.loading.form = false;
                self.$router.push(self.projectListPath);
            });
        },
        selectImage() {
            this.$refs.changeImage.click();
        },
        removeImage() {
            this.project.image = null;
            this.project.image_preview = null;
            this.imageRemoved = true;
            if (this.$refs.changeImage) {
                this.$refs.changeImage.value = '';
            }
        },
        changeImage(event) {
            const self = this;
            if (!event.target.files.length) {
                return;
            }

            const file = event.target.files[0];
            if (['image/png', 'image/x-citrix-png', 'image/x-png', 'image/jpeg', 'image/x-citrix-jpeg'].includes(file.type)) {
                self.project.image = file;
                self.project.image_preview = URL.createObjectURL(file);
                self.imageRemoved = false;
                return;
            }

            self.$notify({
                title: self.$i18n.t('Unsupported file type').toString(),
                text: self.$i18n.t('Only image type files are accepted').toString(),
                type: 'error',
            });
            event.target.value = '';
        },
        generateFallbackImage() {
            const seedText = String(this.project.title || 'P').trim() || 'P';
            const firstChar = seedText.charAt(0).toUpperCase();
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80" width="80" height="80"><defs><linearGradient id="projectGradientEditDashboard" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="hsl(210 35% 48%)"/><stop offset="100%" stop-color="hsl(210 35% 34%)"/></linearGradient></defs><rect width="80" height="80" fill="url(#projectGradientEditDashboard)"/><text x="50%" y="54%" text-anchor="middle" dominant-baseline="middle" fill="rgba(255,255,255,0.55)" font-family="Arial, sans-serif" font-size="34" font-weight="700">${firstChar}</text></svg>`;
            return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
        },
        saveProject() {
            const self = this;
            self.loading.form = true;

            const formData = new FormData();
            formData.append('_method', 'PUT');
            formData.append('title', self.project.title || '');
            formData.append('description', self.project.description || '');
            formData.append('remove_image', self.imageRemoved ? '1' : '0');
            if (self.project.image instanceof File) {
                formData.append('image', self.project.image);
            }

            axios.post('api/workspace/projects/' + self.$route.params.id, formData, {
                headers: {'Content-Type': 'multipart/form-data'},
            }).then(function (response) {
                const projectData = response.data && response.data.project ? response.data.project : null;
                if (projectData) {
                    self.project.delete_guard = projectData.delete_guard || self.project.delete_guard;
                }
                self.loading.form = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data updated correctly').toString(),
                    type: 'success',
                });
                self.$router.push(self.projectListPath);
            }).catch(function () {
                self.loading.form = false;
            });
        },
        deleteProject() {
            const self = this;
            if (!self.canDeleteProject) {
                return;
            }
            self.loading.form = true;
            axios.delete('api/workspace/projects/' + self.$route.params.id).then(function () {
                self.loading.form = false;
                self.deleteProjectModal = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data deleted successfully').toString(),
                    type: 'success',
                });
                self.$router.push(self.projectListPath);
            }).catch(function (error) {
                self.loading.form = false;
                self.deleteProjectModal = false;
                if (error && error.response && error.response.status === 422 && error.response.data && error.response.data.delete_guard) {
                    self.project.delete_guard = error.response.data.delete_guard;
                }
            });
        },
        openDeleteProjectModal() {
            if (!this.canDeleteProject) {
                return;
            }

            this.deleteProjectModal = true;
        },
    },
};
</script>

<style lang="scss">
@reference "../../../../../sass/tailwind-reference.css";

.avatar-editor {
    .avatar-editor-remove {
        @apply hidden;
    }

    &:hover {
        .avatar-editor-remove {
            @apply inline-flex;
        }
    }
}
</style>
